'use client';

import { useSyncExternalStore } from 'react';

type Listener = () => void;

// biome-ignore lint/style/noInferrableTypes: module-level state must stay mutable boolean
let hasHydrated: boolean = false;
const listeners = new Set<Listener>();
// biome-ignore lint/style/noInferrableTypes: module-level state must stay mutable boolean
let notificationScheduled: boolean = false;

const notifyAll = () => {
  for (const listener of listeners) {
    listener();
  }
  listeners.clear();
};

const scheduleNotification = () => {
  if (notificationScheduled) {
    return;
  }

  notificationScheduled = true;

  const run = () => {
    notificationScheduled = false;
    if (!hasHydrated) {
      hasHydrated = true;
    }

    if (listeners.size === 0) {
      return;
    }

    notifyAll();
  };

  if (typeof queueMicrotask === 'function') {
    queueMicrotask(run);
  } else {
    Promise.resolve()
      .then(run)
      .catch(() => {
        // Ignore microtask errors; hydration notifications are best-effort.
      });
  }
};

const subscribe = (callback: Listener) => {
  if (hasHydrated) {
    callback();
    return () => {
      // No subscription necessary if hydration already occurred.
    };
  }

  listeners.add(callback);
  scheduleNotification();

  return () => {
    listeners.delete(callback);
  };
};

const getClientSnapshot = () => hasHydrated;
const getServerSnapshot = () => false;

/**
 * Returns `true` once the component has mounted on the client.
 * Uses `useSyncExternalStore` to avoid wiring imperative effects solely for hydration checks.
 */
export function useIsClient(): boolean {
  return useSyncExternalStore(subscribe, getClientSnapshot, getServerSnapshot);
}
